import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { rankingsAPI } from '../services/api';

const RankingSkeleton = () => (
  <tr data-testid="ranking-skeleton-row" className="border-b border-gray-800/50">
    {[...Array(6)].map((_, i) => (
      <td key={i} className="py-4 px-3">
        <div className="h-5 bg-gray-800/60 rounded relative overflow-hidden">
          <div className="skeleton-shimmer absolute inset-0" />
        </div>
      </td>
    ))}
  </tr>
);

const RankBadge = ({ rank }) => {
  const colors = {
    1: 'from-yellow-500 to-yellow-600 shadow-yellow-500/30',
    2: 'from-gray-300 to-gray-400 shadow-gray-300/30',
    3: 'from-amber-600 to-amber-700 shadow-amber-600/30',
  };

  if (rank <= 3) {
    return (
      <span
        data-testid={`rank-badge-${rank}`}
        className={`inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br ${colors[rank]} text-battle-dark font-black text-sm shadow-lg`}
      >
        {rank}
      </span>
    );
  }

  return (
    <span
      data-testid={`rank-badge-${rank}`}
      className="text-gray-500 font-bold text-sm"
    >
      {rank}
    </span>
  );
};

const StreakIndicator = ({ streak }) => {
  if (!streak || streak === 0) return <span className="text-gray-600">-</span>;
  if (streak > 0) {
    return (
      <span className="text-green-400 font-bold text-xs flex items-center gap-0.5">
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
        </svg>
        W{streak}
      </span>
    );
  }
  return (
    <span className="text-red-400 font-bold text-xs flex items-center gap-0.5">
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
      </svg>
      L{Math.abs(streak)}
    </span>
  );
};

const Rankings = () => {
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null); // eslint-disable-line no-unused-vars

  useEffect(() => {
    fetchRankings();
  }, []);

  const fetchRankings = async () => {
    try {
      const response = await rankingsAPI.getAll();
      setRankings(response.data.rankings || []);
      setMeta(response.data.meta);
    } catch (error) {
      console.error('Error fetching rankings:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-12 md:py-16 px-4">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(74,93,58,0.12)_0%,transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tight text-white mb-3">
            <span className="text-brand-yellow">RANK</span>INGS
          </h1>
          <p className="text-gray-500 text-base md:text-lg max-w-xl mx-auto">
            Community-voted ELO rankings. Every vote shapes the leaderboard.
          </p>
        </div>
      </section>

      {/* Rankings Table */}
      <section className="max-w-5xl mx-auto px-4 pb-16">
        {loading ? (
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800 text-left">
                  <th className="py-3 px-3 text-gray-500 text-xs font-bold uppercase tracking-wider">#</th>
                  <th className="py-3 px-3 text-gray-500 text-xs font-bold uppercase tracking-wider">Artist</th>
                  <th className="py-3 px-3 text-gray-500 text-xs font-bold uppercase tracking-wider">ELO</th>
                  <th className="py-3 px-3 text-gray-500 text-xs font-bold uppercase tracking-wider">Record</th>
                  <th className="py-3 px-3 text-gray-500 text-xs font-bold uppercase tracking-wider hidden sm:table-cell">Win%</th>
                  <th className="py-3 px-3 text-gray-500 text-xs font-bold uppercase tracking-wider hidden md:table-cell">Streak</th>
                </tr>
              </thead>
              <tbody>
                {[...Array(8)].map((_, i) => (
                  <RankingSkeleton key={i} />
                ))}
              </tbody>
            </table>
          </div>
        ) : rankings.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-battle-gray border border-gray-800 flex items-center justify-center">
              <svg className="w-10 h-10 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M18.75 4.236c.982.143 1.954.317 2.916.52A6.003 6.003 0 0016.27 9.728M18.75 4.236V4.5c0 2.108-.966 3.99-2.48 5.228m0 0a6.003 6.003 0 01-5.54 0" />
              </svg>
            </div>
            <p className="text-gray-400 text-xl font-bold mb-2">No ranked artists yet</p>
            <p className="text-gray-600 text-sm max-w-md mx-auto">
              Vote on battles to build the rankings.
            </p>
          </div>
        ) : (
          <div className="card overflow-hidden fade-in-content">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800 text-left">
                    <th className="py-3 px-3 text-gray-500 text-xs font-bold uppercase tracking-wider w-12">#</th>
                    <th className="py-3 px-3 text-gray-500 text-xs font-bold uppercase tracking-wider">Artist</th>
                    <th className="py-3 px-3 text-gray-500 text-xs font-bold uppercase tracking-wider">ELO</th>
                    <th className="py-3 px-3 text-gray-500 text-xs font-bold uppercase tracking-wider">Record</th>
                    <th className="py-3 px-3 text-gray-500 text-xs font-bold uppercase tracking-wider hidden sm:table-cell">Win%</th>
                    <th className="py-3 px-3 text-gray-500 text-xs font-bold uppercase tracking-wider hidden md:table-cell">Streak</th>
                  </tr>
                </thead>
                <tbody>
                  {rankings.map((entry) => (
                    <tr
                      key={entry.artist_id}
                      className={`border-b border-gray-800/50 hover:bg-battle-gray-light/50 transition-colors ${
                        entry.rank <= 3 ? 'bg-battle-gray/30' : ''
                      }`}
                    >
                      <td className="py-4 px-3">
                        <RankBadge rank={entry.rank} />
                      </td>
                      <td className="py-4 px-3">
                        <Link
                          to={`/artist/${entry.artist.id}`}
                          data-testid={`ranking-artist-link-${entry.artist.id}`}
                          className="flex items-center gap-3 group"
                        >
                          {entry.artist.avatar_url ? (
                            <img
                              src={entry.artist.avatar_url}
                              alt={entry.artist.display_name || entry.artist.username}
                              className="w-9 h-9 rounded-full border border-gray-700 group-hover:border-brand-yellow/50 transition-colors"
                            />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-army/20 border border-army/30 flex items-center justify-center text-brand-yellow font-bold text-sm">
                              {(entry.artist.display_name || entry.artist.username).charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className="text-white font-bold text-sm uppercase tracking-tight font-display group-hover:text-brand-yellow transition-colors">
                            {entry.artist.display_name || entry.artist.username}
                          </span>
                        </Link>
                      </td>
                      <td className="py-4 px-3">
                        <span className="text-white font-black text-base">{entry.elo_rating}</span>
                      </td>
                      <td className="py-4 px-3">
                        <span className="text-gray-300 font-semibold text-sm">
                          {entry.wins}-{entry.losses}
                        </span>
                      </td>
                      <td className="py-4 px-3 hidden sm:table-cell">
                        <span className="text-gray-400 text-sm">{entry.win_percentage}%</span>
                      </td>
                      <td className="py-4 px-3 hidden md:table-cell">
                        <StreakIndicator streak={entry.current_streak} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default Rankings;
